import { useState, useEffect, useCallback, useMemo } from "react";
import { useLiveData } from '../LiveDataContext';

var API = 'https://api.algrok.com';
function fmtRs(n){if(n==null||isNaN(n))return'--';return(n>=0?'+':'')+Math.round(n).toLocaleString('en-IN')+'₹';}
function pc(n){return n>=0?'#00d4aa':'#ff4466';}
function fmtY(v){if(Math.abs(v)>=1000)return(v>=0?'+':'')+ (v/1000).toFixed(1)+'k';return Math.round(v).toString();}

var SHAPE_LABELS={'none':'No Position','butterfly':'🦋 Iron Butterfly','straddle':'🦋 Naked Straddle','shifted_bull':'⇈ Shifted Bull','shifted_bear':'⇊ Shifted Bear'};

function useWindowWidth(){
  var get=function(){return window.innerWidth;};
  var [w,setW]=useState(get());
  useEffect(function(){var h=function(){setW(get());};window.addEventListener('resize',h);return function(){window.removeEventListener('resize',h);};},[]);
  return w;
}

/* ═════════════════════════════════════════════
   BLACK-SCHOLES HELPERS (in-browser pricing)
   ═════════════════════════════════════════════ */
function bsN(x){
  var a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=0.3275911;
  var sign=x<0?-1:1;x=Math.abs(x)/Math.sqrt(2);
  var t=1.0/(1.0+p*x);
  var y=1.0-(((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-x*x);
  return 0.5*(1.0+sign*y);
}
function bsPrice(S,K,T,r,sigma,type){
  if(T<=0||sigma<=0||S<=0||K<=0){return type==='CE'?Math.max(0,S-K):Math.max(0,K-S);}
  var sqT=Math.sqrt(T);
  var d1=(Math.log(S/K)+(r+sigma*sigma/2)*T)/(sigma*sqT);
  var d2=d1-sigma*sqT;
  if(type==='CE')return S*bsN(d1)-K*Math.exp(-r*T)*bsN(d2);
  return K*Math.exp(-r*T)*bsN(-d2)-S*bsN(-d1);
}
function impliedVol(price,S,K,T,r,type){
  if(price<=0||T<=0||S<=0)return 0.15;
  var intrinsic=type==='CE'?Math.max(0,S-K):Math.max(0,K-S);
  if(price<=intrinsic+0.05)return 0.05;
  var low=0.01,high=3.0;
  for(var i=0;i<55;i++){
    var mid=(low+high)/2;
    var p=bsPrice(S,K,T,r,mid,type);
    if(Math.abs(p-price)<0.02)return mid;
    if(p<price)low=mid;else high=mid;
  }
  return Math.max(0.05,Math.min(2.5,(low+high)/2));
}
function parseExpiry(label){
  if(!label)return null;
  if(/^\d{4}-\d{2}-\d{2}/.test(label))return new Date(label.slice(0,10)+'T15:30:00+05:30');
  var m=label.toUpperCase().match(/(\d{1,2})[\s\-]*([A-Z]{3})[\s\-]*(\d{2,4})/);
  if(m){
    var months={JAN:0,FEB:1,MAR:2,APR:3,MAY:4,JUN:5,JUL:6,AUG:7,SEP:8,OCT:9,NOV:10,DEC:11};
    var day=+m[1],mo=months[m[2]],yr=+m[3];
    if(yr<100)yr+=2000;
    if(mo!=null)return new Date(yr,mo,day,15,30,0);
  }
  return null;
}
function computeDTE(expiryLabel,indexName){
  var exp=parseExpiry(expiryLabel);
  if(exp){
    var diffMs=exp.getTime()-Date.now();
    return Math.max(0.001,diffMs/(1000*60*60*24));
  }
  // Fallback: next weekly expiry per index (NIFTY/FINNIFTY=Tue, SENSEX=Fri)
  var now=new Date();
  var dow=now.getDay();
  var targetDow=indexName==='SENSEX'?5:2;
  var daysAhead=(targetDow-dow+7)%7;
  if(daysAhead===0)daysAhead=0.3;
  return daysAhead;
}

/* ═════════════════════════════════════════════
   HERO PAYOFF CHART — dual-curve (today + at expiry)
   Bold cyan = today's expected P&L (BS-priced, current IV & DTE)
   Faded dotted gray = expiry intrinsic payoff (reference)
   Profit/loss zones + breakevens + spot dot all based on TODAY's curve.
   ═════════════════════════════════════════════ */
function PayoffChart({state,isCompact,isTight}){
  var atm=state.atm||state.spot||0;
  var spot=state.spot||0;
  var legs=state.legs||[];
  var open=legs.filter(function(l){return l.status==='open'&&l.entry_price;});
  var closed=legs.filter(function(l){return l.status==='closed'&&l.entry_price&&l.exit_price;});
  var sells=open.filter(function(l){return l.side==='SELL';});
  var buys=open.filter(function(l){return l.side==='BUY';});

  // Realized P&L (roll cost from closed legs)
  var realized=0;
  closed.forEach(function(l){
    if(l.side==='SELL')realized+=(l.entry_price-l.exit_price)*l.qty;
    else realized+=(l.exit_price-l.entry_price)*l.qty;
  });

  if(!sells.length||!atm){
    return <div style={{background:'rgba(13,21,38,0.8)',borderRadius:8,padding:30,border:'1px solid rgba(26,45,74,0.4)',marginBottom:14,textAlign:'center',color:'#556677',fontSize:12}}>Payoff chart will appear once position is open</div>;
  }

  // ── Compute DTE and IV per leg (T-0 IV from current LTP) ──
  var dteDays=computeDTE(state.expiry_label,(state.config&&state.config.index)||'NIFTY');
  var T=dteDays/365;
  var r=0.065;
  var legIVs=open.map(function(l){
    if(spot>0&&l.ltp>0&&l.strike>0&&T>0){
      return impliedVol(l.ltp,spot,l.strike,T,r,l.type);
    }
    return 0.15;
  });

  // ── Build payoff curves: both today (T-priced) and expiry (intrinsic) ──
  var range=700, stepX=5;
  var pointsExpiry=[];
  var pointsToday=[];
  for(var xv=atm-range;xv<=atm+range;xv+=stepX){
    // Expiry P&L: intrinsic value at each leg
    var plE=realized;
    sells.forEach(function(l){
      var intr=l.type==='CE'?Math.max(0,xv-l.strike):Math.max(0,l.strike-xv);
      plE+=(l.entry_price-intr)*l.qty;
    });
    buys.forEach(function(l){
      var intr=l.type==='CE'?Math.max(0,xv-l.strike):Math.max(0,l.strike-xv);
      plE+=(intr-l.entry_price)*l.qty;
    });
    pointsExpiry.push({x:xv,y:Math.round(plE)});

    // Today P&L: BS price with current T and each leg's IV
    var plT=realized;
    open.forEach(function(l,idx){
      var price=bsPrice(xv,l.strike,T,r,legIVs[idx],l.type);
      if(l.side==='SELL')plT+=(l.entry_price-price)*l.qty;
      else plT+=(price-l.entry_price)*l.qty;
    });
    pointsToday.push({x:xv,y:Math.round(plT)});
  }

  // Today stats
  var maxT=Math.max.apply(null,pointsToday.map(function(p){return p.y;}));
  var minT=Math.min.apply(null,pointsToday.map(function(p){return p.y;}));
  var maxE=Math.max.apply(null,pointsExpiry.map(function(p){return p.y;}));

  // Today breakevens (linear interp where y crosses zero)
  var breakevens=[];
  for(var i=1;i<pointsToday.length;i++){
    var a=pointsToday[i-1].y,b=pointsToday[i].y;
    if((a>0&&b<=0)||(a<0&&b>=0)||(a===0&&b!==0)){
      var dx=pointsToday[i].x-pointsToday[i-1].x;
      var dy=b-a;
      var bex=dy===0?pointsToday[i].x:Math.round(pointsToday[i-1].x-a*dx/dy);
      breakevens.push(bex);
    }
  }

  // Current P&L (today's curve at current spot)
  var currentPnl=0;
  for(var j=0;j<pointsToday.length-1;j++){
    if(pointsToday[j].x<=spot&&pointsToday[j+1].x>=spot){
      var dx2=pointsToday[j+1].x-pointsToday[j].x;
      var dy2=pointsToday[j+1].y-pointsToday[j].y;
      currentPnl=Math.round(pointsToday[j].y+(spot-pointsToday[j].x)*(dx2?dy2/dx2:0));
      break;
    }
  }

  // SVG dimensions
  var W=1200, H=320;
  var padL=58, padR=20, padT=34, padB=42;
  var chartW=W-padL-padR;
  var chartH=H-padT-padB;
  var xMin=atm-range, xMax=atm+range;
  // Scale based on the larger of today/expiry to keep both readable
  var allYs=pointsToday.map(function(p){return p.y;}).concat(pointsExpiry.map(function(p){return p.y;}));
  var yAbs=Math.max.apply(null,allYs.map(function(y){return Math.abs(y);}))*1.15||100;
  var yMin=-yAbs, yMax=yAbs;

  function xS(x){return padL+((x-xMin)/(xMax-xMin))*chartW;}
  function yS(y){return padT+((yMax-y)/(yMax-yMin))*chartH;}
  var zeroY=yS(0);

  // Line strings
  var todayLine=pointsToday.map(function(p){return xS(p.x).toFixed(1)+','+yS(p.y).toFixed(1);}).join(' ');
  var expiryLine=pointsExpiry.map(function(p){return xS(p.x).toFixed(1)+','+yS(p.y).toFixed(1);}).join(' ');

  // Profit/loss area paths (based on today's curve)
  var profitPts=[xS(pointsToday[0].x)+','+zeroY];
  pointsToday.forEach(function(p){var y=p.y>0?yS(p.y):zeroY;profitPts.push(xS(p.x)+','+y);});
  profitPts.push(xS(pointsToday[pointsToday.length-1].x)+','+zeroY);

  var lossPts=[xS(pointsToday[0].x)+','+zeroY];
  pointsToday.forEach(function(p){var y=p.y<0?yS(p.y):zeroY;lossPts.push(xS(p.x)+','+y);});
  lossPts.push(xS(pointsToday[pointsToday.length-1].x)+','+zeroY);

  // X-axis ticks every 100pts
  var xTicks=[];
  var firstTick=Math.ceil(xMin/100)*100;
  for(var tx=firstTick;tx<=xMax;tx+=100){xTicks.push(tx);}

  // Y-axis ticks
  var yTicks=[yMin,yMin*0.5,0,yMax*0.5,yMax];

  // Average IV for header display
  var avgIV=legIVs.length?(legIVs.reduce(function(a,b){return a+b;},0)/legIVs.length*100):0;

  return (
    <div style={{background:'rgba(13,21,38,0.85)',borderRadius:8,padding:isCompact?12:16,border:'1px solid rgba(26,45,74,0.5)',marginBottom:14}}>
      {/* Header stats */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,flexWrap:'wrap',gap:8}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{fontSize:12,fontWeight:800,color:'#e2e8f0',textTransform:'uppercase',letterSpacing:0.5}}>Payoff</div>
          {state.position_entry_spot>0&&<div style={{fontSize:9,color:'#556677'}}>Entry spot {Math.round(state.position_entry_spot)} · ATM {state.atm} · DTE {dteDays.toFixed(1)} · IV ⌀{avgIV.toFixed(0)}%</div>}
        </div>
        <div style={{display:'flex',gap:isTight?8:14,fontSize:10,flexWrap:'wrap'}}>
          <div><span style={{color:'#556677'}}>Today max </span><span style={{color:'#00d4aa',fontWeight:800}}>{fmtRs(maxT)}</span></div>
          <div><span style={{color:'#556677'}}>Today min </span><span style={{color:'#ff4466',fontWeight:800}}>{fmtRs(minT)}</span></div>
          {breakevens.length>0&&<div><span style={{color:'#556677'}}>BE </span><span style={{color:'#fbbf24',fontWeight:800}}>{breakevens.join(' – ')}</span></div>}
          <div><span style={{color:'#556677'}}>At spot {spot?Math.round(spot):'--'} </span><span style={{color:pc(currentPnl),fontWeight:800,fontSize:11}}>{fmtRs(currentPnl)}</span></div>
          <div><span style={{color:'#556677'}}>Expiry max </span><span style={{color:'#4a6080'}}>{fmtRs(maxE)}</span></div>
        </div>
      </div>

      {/* Chart SVG */}
      <svg viewBox={'0 0 '+W+' '+H} preserveAspectRatio="none" style={{width:'100%',height:isTight?200:isCompact?240:280,display:'block'}}>
        <defs>
          <linearGradient id="ssProfitG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00d4aa" stopOpacity="0.42"/>
            <stop offset="100%" stopColor="#00d4aa" stopOpacity="0.04"/>
          </linearGradient>
          <linearGradient id="ssLossG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff4466" stopOpacity="0.04"/>
            <stop offset="100%" stopColor="#ff4466" stopOpacity="0.42"/>
          </linearGradient>
        </defs>

        {/* Y-axis grid + labels */}
        {yTicks.map(function(t,i){
          var y=yS(t);
          var zero=Math.abs(t)<0.01;
          return <g key={'y'+i}>
            <line x1={padL} y1={y} x2={W-padR} y2={y} stroke={zero?'rgba(140,160,190,0.45)':'rgba(26,45,74,0.32)'} strokeWidth={zero?1.3:1} strokeDasharray={zero?'none':'3 4'}/>
            <text x={padL-8} y={y+3} fill={zero?'#8899aa':'#556677'} fontSize="10" textAnchor="end" fontWeight={zero?700:400}>{zero?'0':fmtY(t)}</text>
          </g>;
        })}

        {/* Today area fills (profit/loss zones) */}
        <polygon points={profitPts.join(' ')} fill="url(#ssProfitG)"/>
        <polygon points={lossPts.join(' ')} fill="url(#ssLossG)"/>

        {/* Expiry curve — dotted faded reference (drawn under today's line) */}
        <polyline points={expiryLine} fill="none" stroke="#4a6080" strokeWidth="1.5" strokeDasharray="5 4" strokeLinejoin="round" opacity="0.65"/>

        {/* Breakeven verticals (today's) */}
        {breakevens.map(function(be,i){
          if(be<xMin||be>xMax)return null;
          var x=xS(be);
          return <g key={'be'+i}>
            <line x1={x} y1={padT} x2={x} y2={H-padB} stroke="#fbbf24" strokeWidth="1" strokeDasharray="4 4" opacity="0.55"/>
            <text x={x} y={padT-3} fill="#fbbf24" fontSize="9" textAnchor="middle" fontWeight={700}>BE {be}</text>
          </g>;
        })}

        {/* Today's curve — primary bold cyan */}
        <polyline points={todayLine} fill="none" stroke="#0ea5e9" strokeWidth="2.8" strokeLinejoin="round" strokeLinecap="round"/>

        {/* X-axis line + ticks */}
        <line x1={padL} y1={H-padB} x2={W-padR} y2={H-padB} stroke="rgba(140,160,190,0.4)" strokeWidth="1"/>
        {xTicks.map(function(tx,i){
          var x=xS(tx);
          var isATM=tx===atm;
          return <g key={'x'+i}>
            <line x1={x} y1={H-padB} x2={x} y2={H-padB+4} stroke={isATM?'#0ea5e9':'rgba(140,160,190,0.4)'}/>
            <text x={x} y={H-padB+18} fill={isATM?'#0ea5e9':'#556677'} fontSize="10" textAnchor="middle" fontWeight={isATM?700:400}>{tx}</text>
          </g>;
        })}

        {/* Strike tags */}
        {sells.concat(buys).map(function(l,i){
          if(l.strike<xMin||l.strike>xMax)return null;
          var x=xS(l.strike);
          var isSell=l.side==='SELL';
          var color=isSell?'#ff8855':'#55d8aa';
          var ty=H-padB+30;
          return <g key={'sk'+i}>
            <polygon points={x+','+(ty-4)+' '+(x-4)+','+(ty+3)+' '+(x+4)+','+(ty+3)} fill={color}/>
            <text x={x} y={ty+12} fill={color} fontSize="9" textAnchor="middle" fontWeight={700}>{isSell?'-':'+'}{l.type}{l.strike}</text>
          </g>;
        })}

        {/* SPOT vertical with P&L dot ON TODAY's curve */}
        {spot>0&&spot>=xMin&&spot<=xMax&&(function(){
          var x=xS(spot);
          var pnlY=yS(currentPnl);
          return <g>
            <line x1={x} y1={padT-12} x2={x} y2={H-padB+24} stroke="#fbbf24" strokeWidth="1.6"/>
            <rect x={x-38} y={padT-24} width="76" height="16" fill="#fbbf24" rx="2"/>
            <text x={x} y={padT-12} fill="#0d1526" fontSize="10" textAnchor="middle" fontWeight={900}>SPOT {Math.round(spot)}</text>
            <circle cx={x} cy={pnlY} r="5" fill="#fbbf24" stroke="#0d1526" strokeWidth="2"/>
          </g>;
        })()}

        {/* Legend (top-left) */}
        <g transform={'translate('+(padL+8)+','+(padT+6)+')'}>
          <rect x="0" y="0" width="170" height="60" fill="rgba(10,15,30,0.78)" stroke="rgba(26,45,74,0.5)" strokeWidth="1" rx="3"/>
          <line x1="6" y1="9" x2="20" y2="9" stroke="#0ea5e9" strokeWidth="2.8"/>
          <text x="26" y="12" fill="#8899aa" fontSize="9">Today (live)</text>
          <line x1="100" y1="9" x2="114" y2="9" stroke="#4a6080" strokeWidth="1.5" strokeDasharray="3 2"/>
          <text x="120" y="12" fill="#8899aa" fontSize="9">At expiry</text>
          <rect x="6" y="20" width="10" height="6" fill="url(#ssProfitG)"/>
          <text x="20" y="26" fill="#8899aa" fontSize="9">Profit zone</text>
          <rect x="78" y="20" width="10" height="6" fill="url(#ssLossG)"/>
          <text x="92" y="26" fill="#8899aa" fontSize="9">Loss zone</text>
          <line x1="6" y1="38" x2="16" y2="38" stroke="#fbbf24" strokeWidth="2"/>
          <text x="20" y="42" fill="#8899aa" fontSize="9">Spot / BE</text>
          <polygon points="84,34 80,42 88,42" fill="#ff8855"/>
          <text x="92" y="42" fill="#8899aa" fontSize="9">Sell</text>
          <polygon points="124,34 120,42 128,42" fill="#55d8aa"/>
          <text x="132" y="42" fill="#8899aa" fontSize="9">Buy (hedge)</text>
        </g>
      </svg>
    </div>
  );
}

/* ═════════════════════════════════════════════
   MAIN PAGE
   ═════════════════════════════════════════════ */
export default function SuperStraddle() {
  var [allState, setAllState] = useState(null);
  var winW = useWindowWidth();
  var isCompact = winW < 1080;
  var isTight = winW < 900;
  var [showSettings, setShowSettings] = useState(false);
  var [draft, setDraft] = useState({});

  var fetchState = useCallback(function() {
    fetch(API + '/super-straddle/state').then(function(r){return r.json();}).then(function(d){if(!d.error)setAllState(d);}).catch(function(){});
  }, []);
  useEffect(function() { fetchState(); var id = setInterval(fetchState, 2000); return function() { clearInterval(id); }; }, [fetchState]);

  var doApi = function(url, body) {
    fetch(API + url, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(body || {}) })
      .then(function(){fetchState();}).catch(function(){});
  };

  if (!allState) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',background:'#080e1d',color:'#556677',fontFamily:"'Inter',sans-serif"}}>Loading Super Straddle...</div>;

  var state = allState;
  var cfg = state.config || {};
  var legs = state.legs || [];
  var events = state.events || [];
  var openLegs = legs.filter(function(l){return l.status==='open';});
  var closedLegs = legs.filter(function(l){return l.status==='closed';});
  var openSells = openLegs.filter(function(l){return l.side==='SELL';});
  var pnl = state.total_pnl || state.unrealized_pnl || 0;
  var realized = state.realized_pnl || 0;
  var unrealized = state.unrealized_pnl || 0;
  var shapeLabel = SHAPE_LABELS[state.current_shape] || state.current_shape || 'No Position';

  // Trigger telemetry for each open SELL leg (so user can see proximity to threshold)
  var triggerStatus = openSells.map(function(l){
    var entrySpot = l.entry_spot || state.position_entry_spot || 0;
    var move = l.type === 'CE' ? (state.spot - entrySpot) : (entrySpot - state.spot);
    var expansion = (l.entry_price && l.ltp) ? ((l.ltp - l.entry_price) / l.entry_price * 100) : 0;
    var itm = l.type === 'CE' ? (state.spot > l.strike ? state.spot - l.strike : 0) : (l.strike > state.spot ? l.strike - state.spot : 0);
    var moveOk = move >= (cfg.move_floor_pt || 50);
    var expOk = expansion >= (cfg.expansion_pct || 20);
    return { leg: l, move: move, expansion: expansion, itm: itm, moveOk: moveOk, expOk: expOk, armed: moveOk && expOk };
  });

  var lbl = {display:'flex',flexDirection:'column',gap:3,fontSize:10,color:'#8899aa',fontFamily:'inherit'};
  var inp = {background:'#0a0f1e',color:'#e2e8f0',border:'1px solid rgba(26,45,74,0.8)',borderRadius:4,padding:'4px 8px',fontSize:11,fontFamily:'inherit',width:'100%'};
  var S = function(key){return function(v){setDraft(function(d){var n={};n[key]=v;return Object.assign({},d,n);});};};

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',background:'#080e1d',color:'#e2e8f0',fontFamily:"'Inter',sans-serif",overflow:'hidden'}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 14px',background:'#0d1526',borderBottom:'1px solid rgba(26,45,74,0.6)',flexShrink:0,flexWrap:'wrap'}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{fontSize:15,fontWeight:800}}>Super Straddle</div>
            <div style={{padding:'2px 7px',borderRadius:4,fontSize:9,fontWeight:800,background:'rgba(14,165,233,0.18)',color:'#0ea5e9',border:'1px solid rgba(14,165,233,0.4)'}}>SS1 · single-leg trigger</div>
          </div>
          <div style={{fontSize:9,color:'#556677'}}>{cfg.index} · {cfg.lots}L · Entry {cfg.entry_time} · Exit {cfg.exit_time_weekly}/{cfg.exit_time_0dte} · Trigger: {cfg.expansion_pct}% exp + {cfg.move_floor_pt}pt move</div>
        </div>
        <div style={{marginLeft:'auto',textAlign:'right'}}>
          <div style={{fontSize:17,fontWeight:900,color:pc(pnl)}}>{fmtRs(pnl)}</div>
          <div style={{fontSize:9,color:'#556677'}}>R:{fmtRs(realized)} U:{fmtRs(unrealized)} | Credit: {fmtRs(state.entry_premium)}</div>
        </div>
        <div style={{padding:'3px 8px',borderRadius:4,fontSize:10,fontWeight:800,background:state.active?'rgba(0,212,170,0.12)':state.enabled?'rgba(251,191,36,0.12)':'rgba(100,100,120,0.12)',color:state.active?'#00d4aa':state.enabled?'#fbbf24':'#8899aa'}}>
          {state.active?'● ACTIVE':state.enabled?'○ ENABLED':'○ DISABLED'}
        </div>
        {!state.active && <button onClick={function(){doApi(state.enabled?'/super-straddle/disable':'/super-straddle/enable');}} style={{padding:'5px 14px',borderRadius:4,fontSize:11,fontWeight:800,cursor:'pointer',fontFamily:'inherit',background:state.enabled?'rgba(251,191,36,0.15)':'rgba(0,212,170,0.15)',color:state.enabled?'#fbbf24':'#00d4aa',border:'1px solid '+(state.enabled?'rgba(251,191,36,0.4)':'rgba(0,212,170,0.4)')}}>{state.enabled?'Disable':'Enable'}</button>}
        {!state.active && <button onClick={function(){if(window.confirm('Force enter Super Straddle now? This bypasses entry time.'))doApi('/super-straddle/enter');}} style={{padding:'5px 14px',borderRadius:4,fontSize:11,fontWeight:800,cursor:'pointer',fontFamily:'inherit',background:'rgba(14,165,233,0.15)',color:'#0ea5e9',border:'1px solid rgba(14,165,233,0.4)'}}>Force Enter</button>}
        {state.active && <button onClick={function(){if(window.confirm('Exit Super Straddle positions?'))doApi('/super-straddle/exit',{reason:'Manual exit'});}} style={{padding:'5px 14px',borderRadius:4,fontSize:11,fontWeight:800,cursor:'pointer',fontFamily:'inherit',background:'rgba(255,68,102,0.15)',color:'#ff4466',border:'1px solid rgba(255,68,102,0.4)'}}>Exit</button>}
        <button onClick={function(){setDraft(cfg);setShowSettings(!showSettings);}} style={{padding:'5px 10px',borderRadius:4,fontSize:11,cursor:'pointer',fontFamily:'inherit',background:'rgba(14,165,233,0.12)',color:'#0ea5e9',border:'1px solid rgba(14,165,233,0.3)',fontWeight:700}}>{'⚙'}</button>
      </div>

      {/* Settings Modal */}
      {showSettings && <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}} onClick={function(){setShowSettings(false);}}>
        <div onClick={function(e){e.stopPropagation();}} style={{background:'#0d1526',borderRadius:12,padding:24,width:560,maxHeight:'85vh',overflowY:'auto',border:'1px solid rgba(26,45,74,0.6)'}}>
          <div style={{fontSize:14,fontWeight:800,marginBottom:4}}>Super Straddle Settings</div>
          <div style={{fontSize:10,color:'#556677',marginBottom:14}}>Single-leg expansion trigger · universal ₹5 premium floor · no expiry-day overrides</div>

          {/* Entry & Exit */}
          <div style={{fontSize:10,fontWeight:700,color:'#556677',textTransform:'uppercase',marginBottom:6}}>Entry & Exit</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:14}}>
            <label style={lbl}>Entry Time<input type="text" value={draft.entry_time||cfg.entry_time||'09:16'} onChange={function(e){S('entry_time')(e.target.value);}} style={inp}/></label>
            <label style={lbl}>Exit (weekly)<input type="text" value={draft.exit_time_weekly||cfg.exit_time_weekly||'15:20'} onChange={function(e){S('exit_time_weekly')(e.target.value);}} style={inp}/></label>
            <label style={lbl}>Exit (0DTE)<input type="text" value={draft.exit_time_0dte||cfg.exit_time_0dte||'15:10'} onChange={function(e){S('exit_time_0dte')(e.target.value);}} style={inp}/></label>
            <label style={lbl}>Lots<input type="number" min="1" value={draft.lots!=null?draft.lots:cfg.lots} onChange={function(e){S('lots')(Math.max(1,+e.target.value));}} style={inp}/></label>
            <label style={lbl}>Product<select value={draft.product||cfg.product||'MIS'} onChange={function(e){S('product')(e.target.value);}} style={inp}><option value="MIS">MIS</option><option value="NRML">NRML</option></select></label>
            <label style={lbl}>Hedge Mode<select value={draft.hedge_mode||cfg.hedge_mode} onChange={function(e){S('hedge_mode')(e.target.value);}} style={inp}><option value="auto_oi">Auto + OI</option><option value="oi_walls">OI Walls</option><option value="fixed">Fixed</option><option value="none">No hedge</option></select></label>
            {(draft.hedge_mode||cfg.hedge_mode)==='fixed'&&<label style={Object.assign({},lbl,{gridColumn:'1 / -1'})}>Wing Width (pts)<input type="number" step="50" value={draft.fixed_wing_width!=null?draft.fixed_wing_width:cfg.fixed_wing_width||400} onChange={function(e){S('fixed_wing_width')(+e.target.value);}} style={inp}/></label>}
          </div>

          {/* Trigger */}
          <div style={{fontSize:10,fontWeight:700,color:'#0ea5e9',textTransform:'uppercase',marginBottom:6}}>Trigger</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
            <label style={lbl}>Expansion % (tested leg)
              <select value={draft.expansion_pct!=null?draft.expansion_pct:(cfg.expansion_pct||20)} onChange={function(e){S('expansion_pct')(+e.target.value);}} style={inp}>
                <option value={15}>15%</option><option value={20}>20% (default)</option><option value={25}>25%</option><option value={30}>30%</option>
              </select>
            </label>
            <label style={lbl}>Move floor (pts)
              <select value={draft.move_floor_pt!=null?draft.move_floor_pt:(cfg.move_floor_pt||50)} onChange={function(e){S('move_floor_pt')(+e.target.value);}} style={inp}>
                <option value={30}>30</option><option value={40}>40</option><option value={50}>50 (default)</option><option value={70}>70</option><option value={100}>100</option>
              </select>
            </label>
          </div>

          {/* Forced Morph */}
          <div style={{fontSize:10,fontWeight:700,color:'#fbbf24',textTransform:'uppercase',marginBottom:6}}>Forced Morph (safety net)</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
            <label style={lbl}>Enabled
              <select value={(draft.forced_morph_enabled!=null?draft.forced_morph_enabled:cfg.forced_morph_enabled)?'on':'off'} onChange={function(e){S('forced_morph_enabled')(e.target.value==='on');}} style={inp}>
                <option value="on">On (default)</option><option value="off">Off</option>
              </select>
            </label>
            <label style={lbl}>Threshold (pts ITM)
              <select value={draft.forced_morph_threshold!=null?draft.forced_morph_threshold:(cfg.forced_morph_threshold||100)} onChange={function(e){S('forced_morph_threshold')(+e.target.value);}} style={inp}>
                <option value={80}>80</option><option value={100}>100 (default)</option><option value={120}>120</option><option value={150}>150</option><option value={200}>200</option>
              </select>
            </label>
          </div>

          {/* Roll Destination */}
          <div style={{fontSize:10,fontWeight:700,color:'#a78bfa',textTransform:'uppercase',marginBottom:6}}>Roll Destination</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:14}}>
            <label style={lbl}>Fixed distance (x)
              <select value={draft.roll_distance_x!=null?draft.roll_distance_x:(cfg.roll_distance_x||100)} onChange={function(e){S('roll_distance_x')(+e.target.value);}} style={inp}>
                <option value={50}>50</option><option value={100}>100 (default)</option>
              </select>
            </label>
            <label style={lbl}>Tested leg target
              <select value={draft.roll_target_type||cfg.roll_target_type||'otm_1'} onChange={function(e){S('roll_target_type')(e.target.value);}} style={inp}>
                <option value="atm">ATM</option><option value="otm_1">OTM-1 (default)</option>
              </select>
            </label>
            <label style={lbl}>Even-adj opposite leg
              <select value={draft.even_adj_target||cfg.even_adj_target||'otm_1'} onChange={function(e){S('even_adj_target')(e.target.value);}} style={inp}>
                <option value="atm">ATM</option><option value="otm_1">OTM-1 (default)</option>
              </select>
            </label>
          </div>

          {/* Cooldown & Floor */}
          <div style={{fontSize:10,fontWeight:700,color:'#556677',textTransform:'uppercase',marginBottom:6}}>Cooldown & Premium Floor</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
            <label style={lbl}>Roll cooldown (sec)
              <select value={draft.roll_cooldown_sec!=null?draft.roll_cooldown_sec:(cfg.roll_cooldown_sec||180)} onChange={function(e){S('roll_cooldown_sec')(+e.target.value);}} style={inp}>
                <option value={60}>60</option><option value={120}>120</option><option value={180}>180 (default)</option><option value={300}>300</option>
              </select>
            </label>
            <label style={lbl}>Premium floor (₹) <span style={{color:'#ff4466',fontSize:9}}>(exit+disable if breached)</span>
              <select value={draft.premium_floor!=null?draft.premium_floor:(cfg.premium_floor||5)} onChange={function(e){S('premium_floor')(+e.target.value);}} style={inp}>
                <option value={3}>3</option><option value={5}>5 (default)</option><option value={7}>7</option><option value={10}>10</option>
              </select>
            </label>
          </div>

          {/* Profit management */}
          <div style={{fontSize:10,fontWeight:700,color:'#556677',textTransform:'uppercase',marginBottom:6}}>Profit Management</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <label style={lbl}>MTM SL ₹/lot <span style={{color:'#ff4466',fontSize:9}}>(0=off)</span><input type="number" min={0} value={draft.mtm_sl_per_lot!=null?draft.mtm_sl_per_lot:cfg.mtm_sl_per_lot||0} onChange={function(e){S('mtm_sl_per_lot')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Target ₹/lot<input type="number" min={0} value={draft.target_profit_per_lot!=null?draft.target_profit_per_lot:cfg.target_profit_per_lot||0} onChange={function(e){S('target_profit_per_lot')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Protect ₹/lot<input type="number" min={0} value={draft.protect_profit_per_lot!=null?draft.protect_profit_per_lot:cfg.protect_profit_per_lot||0} onChange={function(e){S('protect_profit_per_lot')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Protect Level ₹/lot<input type="number" min={0} value={draft.protect_profit_level!=null?draft.protect_profit_level:cfg.protect_profit_level||0} onChange={function(e){S('protect_profit_level')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Trail Step ₹/lot<input type="number" min={0} value={draft.trail_protect_per_lot!=null?draft.trail_protect_per_lot:cfg.trail_protect_per_lot||0} onChange={function(e){S('trail_protect_per_lot')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Leg SL Multiple<input type="number" step="0.1" value={draft.leg_sl_multiple!=null?draft.leg_sl_multiple:cfg.leg_sl_multiple||2} onChange={function(e){S('leg_sl_multiple')(+e.target.value);}} style={inp}/></label>
            <label style={lbl}>Max Loss x<input type="number" step="0.1" value={draft.max_loss_multiple!=null?draft.max_loss_multiple:cfg.max_loss_multiple||1.5} onChange={function(e){S('max_loss_multiple')(+e.target.value);}} style={inp}/></label>
            <label style={lbl}>Profit Target %<input type="number" value={draft.profit_target_pct!=null?draft.profit_target_pct:cfg.profit_target_pct||60} onChange={function(e){S('profit_target_pct')(+e.target.value);}} style={inp}/></label>
          </div>

          <div style={{display:'flex',gap:10,marginTop:18}}>
            <button onClick={function(){setShowSettings(false);}} style={{flex:1,padding:10,borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:'rgba(100,100,120,0.1)',color:'#8899aa',border:'1px solid rgba(100,100,120,0.3)'}}>Cancel</button>
            <button onClick={function(){doApi('/super-straddle/config',draft);setShowSettings(false);}} style={{flex:1,padding:10,borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:'rgba(0,212,170,0.15)',color:'#00d4aa',border:'1px solid rgba(0,212,170,0.4)'}}>Save</button>
          </div>
        </div>
      </div>}

      {/* Body scroll */}
      <div style={{flex:1,overflowY:'auto',padding:'10px 14px'}}>

        {/* HERO: Full-width Payoff Chart */}
        <PayoffChart state={state} isCompact={isCompact} isTight={isTight}/>

        {/* Status row */}
        <div style={{display:'grid',gridTemplateColumns:isTight?'repeat(3,1fr)':isCompact?'repeat(4,1fr)':'repeat(6,1fr)',gap:8,marginBottom:12}}>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>SPOT</div>
            <div style={{fontSize:13,fontWeight:800,color:'#0ea5e9'}}>{state.spot||'--'}</div>
          </div>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>ATM</div>
            <div style={{fontSize:13,fontWeight:800,color:'#e2e8f0'}}>{state.atm||'--'}</div>
          </div>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>ENTRY SPOT</div>
            <div style={{fontSize:13,fontWeight:800,color:'#8899aa'}}>{state.position_entry_spot||'--'}</div>
          </div>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>SHAPE</div>
            <div style={{fontSize:11,fontWeight:700,color:'#e2e8f0'}}>{shapeLabel}</div>
          </div>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>ADJUSTMENTS</div>
            <div style={{fontSize:13,fontWeight:800,color:'#e2e8f0'}}>{state.adjustment_count||0} <span style={{fontSize:9,color:'#556677',fontWeight:400}}>(CE:{state.ce_active||0} PE:{state.pe_active||0})</span></div>
          </div>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>NET DELTA</div>
            <div style={{fontSize:13,fontWeight:800,color:Math.abs(state.net_delta||0)<0.1?'#556677':pc(state.net_delta||0)}}>{state.net_delta?state.net_delta.toFixed(3):'0'}</div>
          </div>
        </div>

        {/* Trigger telemetry — per-leg proximity to trigger */}
        {triggerStatus.length>0 && <div style={{background:'rgba(13,21,38,0.8)',borderRadius:8,padding:12,marginBottom:12,border:'1px solid rgba(26,45,74,0.4)'}}>
          <div style={{fontSize:10,fontWeight:800,color:'#556677',marginBottom:8,textTransform:'uppercase'}}>TRIGGER STATUS — needs both ≥2 conditions met to fire</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
            <thead><tr style={{color:'#3d5068'}}>
              <th style={{textAlign:'left',padding:'2px 6px',fontWeight:700}}>Leg</th>
              <th style={{textAlign:'right',padding:'2px 6px',fontWeight:700}}>Spot Move</th>
              <th style={{textAlign:'right',padding:'2px 6px',fontWeight:700}}>Expansion %</th>
              <th style={{textAlign:'right',padding:'2px 6px',fontWeight:700}}>ITM Depth</th>
              <th style={{textAlign:'center',padding:'2px 6px',fontWeight:700}}>Status</th>
            </tr></thead>
            <tbody>{triggerStatus.map(function(ts,i){
              var l=ts.leg;
              return <tr key={i} style={{borderTop:'1px solid rgba(26,45,74,0.2)'}}>
                <td style={{padding:'4px 6px',color:'#e2e8f0'}}>{l.type} {l.strike}</td>
                <td style={{padding:'4px 6px',textAlign:'right',color:ts.moveOk?'#00d4aa':'#8899aa',fontWeight:ts.moveOk?700:400}}>{ts.move.toFixed(0)}pt / {cfg.move_floor_pt||50}pt</td>
                <td style={{padding:'4px 6px',textAlign:'right',color:ts.expOk?'#00d4aa':'#8899aa',fontWeight:ts.expOk?700:400}}>{ts.expansion.toFixed(1)}% / {cfg.expansion_pct||20}%</td>
                <td style={{padding:'4px 6px',textAlign:'right',color:ts.itm>=(cfg.forced_morph_threshold||100)?'#ff4466':ts.itm>0?'#fbbf24':'#556677',fontWeight:ts.itm>0?700:400}}>{ts.itm>0?ts.itm.toFixed(0)+'pt':'—'}</td>
                <td style={{padding:'4px 6px',textAlign:'center'}}>{ts.armed?<span style={{padding:'2px 6px',borderRadius:3,fontSize:9,fontWeight:800,background:'rgba(255,68,102,0.18)',color:'#ff4466',border:'1px solid rgba(255,68,102,0.4)'}}>ARMED</span>:<span style={{color:'#556677',fontSize:9}}>idle</span>}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>}

        {/* Open Legs */}
        {openLegs.length > 0 && (
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:800,color:'#556677',marginBottom:6}}>OPEN LEGS ({openLegs.length})</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead><tr style={{background:'#0d1526'}}>
                {['Label','Symbol','Side','Strike','Entry','LTP','P&L'].map(function(h){return <th key={h} style={{padding:'5px 6px',textAlign:'left',color:'#556677',fontWeight:700,borderBottom:'1px solid rgba(26,45,74,0.4)'}}>{h}</th>;})}
              </tr></thead>
              <tbody>{openLegs.map(function(leg,i){
                var legPnl=0;
                if(leg.entry_price&&leg.ltp){
                  legPnl=leg.side==='SELL'?(leg.entry_price-leg.ltp)*leg.qty:(leg.ltp-leg.entry_price)*leg.qty;
                }
                return <tr key={i} style={{borderBottom:'1px solid rgba(26,45,74,0.12)'}}>
                  <td style={{padding:'5px 6px',color:'#8899aa'}}>{leg.label||''}</td>
                  <td style={{padding:'5px 6px',color:'#e2e8f0',fontWeight:700}}>{(leg.symbol||'').slice(-15)} <span style={{fontSize:8,padding:'0 3px',borderRadius:2,background:leg.type==='CE'?'rgba(0,212,170,0.12)':'rgba(255,68,102,0.12)',color:leg.type==='CE'?'#00d4aa':'#ff4466'}}>{leg.type}</span></td>
                  <td style={{padding:'5px 6px',color:leg.side==='SELL'?'#ff4466':'#00d4aa',fontWeight:700}}>{leg.side}</td>
                  <td style={{padding:'5px 6px',color:'#8899aa'}}>{leg.strike}</td>
                  <td style={{padding:'5px 6px',color:'#e2e8f0'}}>{'₹'}{leg.entry_price?leg.entry_price.toFixed(1):'--'}</td>
                  <td style={{padding:'5px 6px',color:'#0ea5e9',fontWeight:700}}>{'₹'}{leg.ltp?leg.ltp.toFixed(1):'--'}</td>
                  <td style={{padding:'5px 6px',color:pc(legPnl),fontWeight:800}}>{fmtRs(legPnl)}</td>
                </tr>;
              })}</tbody>
            </table>
          </div>
        )}

        {/* Closed Legs */}
        {closedLegs.length > 0 && (
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:800,color:'#556677',marginBottom:6}}>CLOSED LEGS ({closedLegs.length}) — Realized: <span style={{color:pc(realized)}}>{fmtRs(realized)}</span></div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead><tr style={{background:'#0d1526'}}>
                {['Label','Symbol','Side','Strike','Entry','Exit','P&L','Time'].map(function(h){return <th key={h} style={{padding:'5px 6px',textAlign:'left',color:'#3d4f63',fontWeight:700,borderBottom:'1px solid rgba(26,45,74,0.25)'}}>{h}</th>;})}
              </tr></thead>
              <tbody>{closedLegs.map(function(leg,i){
                var legPnl=0;
                if(leg.entry_price&&leg.exit_price){
                  legPnl=leg.side==='SELL'?(leg.entry_price-leg.exit_price)*leg.qty:(leg.exit_price-leg.entry_price)*leg.qty;
                }
                return <tr key={i} style={{borderBottom:'1px solid rgba(26,45,74,0.08)',opacity:0.7}}>
                  <td style={{padding:'4px 6px',color:'#556677'}}>{leg.label||''}</td>
                  <td style={{padding:'4px 6px',color:'#8899aa'}}>{(leg.symbol||'').slice(-15)} <span style={{fontSize:8,padding:'0 3px',borderRadius:2,background:leg.type==='CE'?'rgba(0,212,170,0.08)':'rgba(255,68,102,0.08)',color:leg.type==='CE'?'#00d4aa':'#ff4466'}}>{leg.type}</span></td>
                  <td style={{padding:'4px 6px',color:leg.side==='SELL'?'#ff4466':'#00d4aa'}}>{leg.side}</td>
                  <td style={{padding:'4px 6px',color:'#556677'}}>{leg.strike}</td>
                  <td style={{padding:'4px 6px',color:'#8899aa'}}>{'₹'}{leg.entry_price?leg.entry_price.toFixed(1):'--'}</td>
                  <td style={{padding:'4px 6px',color:'#e2e8f0',fontWeight:700}}>{'₹'}{leg.exit_price?leg.exit_price.toFixed(1):'--'}</td>
                  <td style={{padding:'4px 6px',color:pc(legPnl),fontWeight:800}}>{fmtRs(legPnl)}</td>
                  <td style={{padding:'4px 6px',color:'#3d4f63',fontSize:9}}>{leg.exit_time||''}</td>
                </tr>;
              })}</tbody>
            </table>
          </div>
        )}

        {/* Activity Log */}
        <div style={{marginTop:10,paddingBottom:10}}>
          <div style={{fontSize:10,fontWeight:800,color:'#556677',marginBottom:6}}>ACTIVITY LOG ({events.length})</div>
          {events.slice().reverse().slice(0,60).map(function(l,i){
            return <div key={i} style={{display:'flex',gap:8,fontSize:10,marginBottom:2}}>
              <span style={{color:'#3d4f63',flexShrink:0,fontSize:9}}>{l.ts}</span>
              <span style={{color:l.type==='error'?'#ff4466':l.type==='success'?'#00d4aa':l.type==='warning'?'#fbbf24':'#6b7a8d'}}>{l.msg}</span>
            </div>;
          })}
          {events.length===0 && <div style={{fontSize:10,color:'#3d5068',fontStyle:'italic'}}>No events yet.</div>}
        </div>
      </div>
    </div>
  );
}
